import {
  type Charge, type ChargeMethod, type ChargeStatus, type ChargesSummary, type CreateCharge, formatBRL, METHOD_LABELS, type Quote,
} from '@dnx/contracts';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, Field, Input, PageHeader, Select, Spinner, Switch, cn } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Ban, Copy, CreditCard, ExternalLink, Landmark, QrCode, Wallet } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';
import { brDate } from './queries.js';

export const chargesQuery = (f: { quoteId?: string; status?: string } = {}) => queryOptions({
  queryKey: ['charges', f.quoteId ?? '', f.status ?? ''],
  queryFn: () => api<Charge[]>(`/charges?${new URLSearchParams({ ...(f.quoteId ? { orcamento: f.quoteId } : {}), ...(f.status ? { status: f.status } : {}) })}`),
});
const summaryQuery = queryOptions({ queryKey: ['charges', 'summary'], queryFn: () => api<ChargesSummary>('/charges/summary') });

export const CHARGE_STATUS: Record<ChargeStatus, [string, 'neutral' | 'primary' | 'warning' | 'success' | 'danger']> = {
  pending: ['Aguardando pagamento', 'primary'], partially_paid: ['Pago em parte', 'primary'], paid: ['Pago', 'success'],
  overdue: ['Em atraso', 'danger'], cancelled: ['Cancelada', 'neutral'], refunded: ['Estornada', 'neutral'],
};
const PAYMENT_STATUS: Record<string, string> = {
  PENDING: 'Aguardando', RECEIVED: 'Recebido', CONFIRMED: 'Confirmado', RECEIVED_IN_CASH: 'Recebido em dinheiro', OVERDUE: 'Vencido',
  REFUNDED: 'Estornado', DELETED: 'Cancelado', AWAITING_RISK_ANALYSIS: 'Em análise',
};
const METHOD_ICON: Record<ChargeMethod, typeof QrCode> = { pix: QrCode, credit_card: CreditCard, boleto: Landmark };
const plusDays = (d: number) => new Date(Date.now() - 3 * 3_600_000 + d * 86_400_000).toISOString().slice(0, 10);

function Copyable({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button size="sm" variant="secondary" onClick={() => { void navigator.clipboard.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); }); }}>
      <Copy className="size-3.5" />{done ? 'Copiado' : label}
    </Button>
  );
}

export function ChargeCard({ charge }: { charge: Charge }) {
  const qc = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const canWrite = me.permissions.includes('billing.payment.write');
  const cancel = useMutation({ mutationFn: () => api<Charge>(`/charges/${charge.id}/cancel`, { method: 'POST' }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['charges'] }) });
  const Icon = METHOD_ICON[charge.method];
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Icon className="size-4 text-fg-subtle" />
        <span className="font-medium text-fg">{METHOD_LABELS[charge.method]}{charge.installments > 1 ? ` em ${charge.installments}x` : ''}</span>
        <Badge tone={CHARGE_STATUS[charge.status][1]}>{CHARGE_STATUS[charge.status][0]}</Badge>
        {charge.sandbox && <Badge tone="warning">Teste (sandbox)</Badge>}
      </div>
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-fg-muted">Vence {brDate(charge.dueDate)}</span>
        <span className="tabular-nums"><strong className="text-lg text-fg">{formatBRL(charge.amountCents)}</strong>{charge.paidCents > 0 && charge.status !== 'paid' && <span className="text-fg-muted"> · pago {formatBRL(charge.paidCents)}</span>}</span>
      </div>
      {charge.installments > 1 && (
        <ol className="grid gap-1 text-[12.5px]">
          {charge.payments.map((p) => (
            <li key={p.number} className="flex justify-between gap-2 text-fg-muted">
              <span>{p.number}ª · {p.dueDate ? brDate(p.dueDate) : '—'}</span>
              <span className={cn('tabular-nums', ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'].includes(p.status) && 'text-success', p.status === 'OVERDUE' && 'text-danger')}>
                {formatBRL(p.valueCents)} · {PAYMENT_STATUS[p.status] ?? p.status}
              </span>
            </li>
          ))}
        </ol>
      )}
      {!['paid', 'cancelled', 'refunded'].includes(charge.status) && (
        <div className="flex flex-wrap gap-2">
          {charge.invoiceUrl && <a href={charge.invoiceUrl} target="_blank" rel="noreferrer"><Button size="sm" variant="secondary"><ExternalLink className="size-3.5" />Abrir fatura</Button></a>}
          {charge.invoiceUrl && <Copyable text={charge.invoiceUrl} label="Copiar link" />}
          {charge.pixPayload && <Copyable text={charge.pixPayload} label="Pix copia e cola" />}
          {canWrite && charge.paidCents === 0 && <Button size="sm" variant="ghost" loading={cancel.isPending} onClick={() => cancel.mutate()}><Ban className="size-3.5" />Cancelar</Button>}
        </div>
      )}
      {cancel.error && <Alert tone="danger">{cancel.error.message}</Alert>}
    </div>
  );
}

/** Na página do orçamento aceito: gerar a cobrança ou acompanhar a atual. */
export function QuoteCharges({ q }: { q: Quote }) {
  const qc = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const canRead = me.permissions.includes('billing.payment.read');
  const canWrite = me.permissions.includes('billing.payment.write');
  const { data } = useQuery({ ...chargesQuery({ quoteId: q.id }), enabled: canRead && q.status === 'accepted' });
  const v = q.current;
  const [method, setMethod] = useState<ChargeMethod>('pix');
  const [installments, setInstallments] = useState(v.maxInstallments);
  const [dueDate, setDueDate] = useState(plusDays(3));
  const [notify, setNotify] = useState(true);
  const create = useMutation({
    mutationFn: () => api<Charge>(`/quotes/${q.id}/charges`, { method: 'POST', body: { method, installments: method === 'pix' ? 1 : installments, dueDate, notify } satisfies CreateCharge }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['charges'] }),
  });
  if (!canRead || q.status !== 'accepted') return null;
  const active = data?.find((c) => !['cancelled', 'refunded'].includes(c.status));
  const amount = method === 'pix' ? v.cashTotalCents : v.totalCents;
  const maxInst = method === 'credit_card' ? v.maxInstallments : 12;
  return (
    <Card>
      <CardHeader title="Cobrança" description="Pelo Asaas da clínica. O link vai ao paciente e aparece na área do paciente." />
      <div className="grid gap-4 p-5">
        {!data ? <Spinner className="size-4 text-fg-subtle" label="Carregando" /> : active ? <ChargeCard charge={active} /> : canWrite ? (
          <>
            <div className="grid gap-2" role="radiogroup" aria-label="Forma de pagamento">
              {(['pix', 'credit_card', 'boleto'] as const).map((m) => {
                const Icon = METHOD_ICON[m];
                return (
                  <button key={m} type="button" role="radio" aria-checked={method === m} onClick={() => { setMethod(m); setInstallments(m === 'credit_card' ? v.maxInstallments : 1); }}
                    className={cn('flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm', method === m ? 'border-primary bg-primary-soft' : 'border-border-strong hover:bg-surface-muted')}>
                    <Icon className="size-4 text-fg-subtle" />
                    <span className="flex-1 font-medium text-fg">{METHOD_LABELS[m]}</span>
                    <span className="tabular-nums text-fg-muted">
                      {m === 'pix' ? `${formatBRL(v.cashTotalCents)} à vista` : m === 'credit_card' ? `até ${v.maxInstallments}x` : formatBRL(v.totalCents)}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {method !== 'pix' && (
                <Field label="Parcelas"><Select value={installments} onChange={(e) => setInstallments(Number(e.target.value))}>
                  {Array.from({ length: maxInst }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}x de {formatBRL(Math.round(amount / n))}</option>)}
                </Select></Field>
              )}
              <Field label={method === 'credit_card' ? 'Vencimento' : 'Vencimento (1ª)'}><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Field>
            </div>
            <Switch checked={notify} onChange={setNotify} label="Mandar o link pelo WhatsApp" />
            {create.error && <Alert tone="danger">{create.error.message}</Alert>}
            <Button onClick={() => create.mutate()} loading={create.isPending}><Wallet className="size-4" />Gerar cobrança de {formatBRL(amount)}</Button>
          </>
        ) : <p className="text-sm text-fg-muted">Nenhuma cobrança gerada.</p>}
      </div>
    </Card>
  );
}

const FILTERS: { value: '' | ChargeStatus; label: string }[] = [
  { value: '', label: 'Todas' }, { value: 'pending', label: 'Aguardando' }, { value: 'overdue', label: 'Em atraso' }, { value: 'partially_paid', label: 'Pago em parte' }, { value: 'paid', label: 'Pagas' },
];

/** Cobranças: o que entrou no mês, o que está em aberto e o que atrasou. */
export function ChargesPage() {
  const [status, setStatus] = useState<'' | ChargeStatus>('');
  const { data, isLoading } = useQuery(chargesQuery({ status: status || undefined }));
  const { data: sum } = useQuery(summaryQuery);
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Cobranças" description="Pix, cartão e boleto pelo Asaas da clínica. A baixa é automática quando o paciente paga." />
      {sum && (
        <div className="mt-7 grid gap-3 sm:grid-cols-3">
          <Card className="p-4"><p className="text-[12px] font-medium tracking-wide text-fg-subtle uppercase">Recebido no mês</p><p className="mt-1 text-xl font-semibold text-success tabular-nums">{formatBRL(sum.receivedMonthCents)}</p></Card>
          <Card className="p-4"><p className="text-[12px] font-medium tracking-wide text-fg-subtle uppercase">A receber</p><p className="mt-1 text-xl font-semibold text-fg tabular-nums">{formatBRL(sum.openCents)}</p></Card>
          <Card className={cn('p-4', sum.overdueCents > 0 && 'border-danger/40')}><p className="text-[12px] font-medium tracking-wide text-fg-subtle uppercase">Em atraso</p><p className="mt-1 text-xl font-semibold text-fg tabular-nums">{formatBRL(sum.overdueCents)}</p></Card>
        </div>
      )}
      <div className="mt-6 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button key={f.value} type="button" aria-pressed={status === f.value} onClick={() => setStatus(f.value)}
            className={cn('h-8 rounded-full border px-3 text-[13px]', status === f.value ? 'border-primary bg-primary-soft text-primary' : 'border-border-strong text-fg-muted hover:text-fg')}>{f.label}</button>
        ))}
      </div>
      {isLoading ? <Spinner className="mt-10 size-5 text-fg-subtle" label="Carregando" /> : data?.length ? (
        <Card className="mt-4 divide-y divide-border overflow-hidden">
          {data.map((c) => {
            const Icon = METHOD_ICON[c.method];
            return (
              <div key={c.id} className="grid gap-2 px-5 py-3.5 sm:grid-cols-[minmax(0,1fr)_170px_160px_140px] sm:items-center">
                <div className="min-w-0">
                  <p className="truncate font-medium text-fg">{c.patientName}</p>
                  <p className="text-[12.5px] text-fg-muted">
                    {c.quoteId ? <Link to="/orcamentos/$id" params={{ id: c.quoteId }} className="font-mono hover:text-fg">{c.quoteCode}</Link> : c.description}
                    {' · '}vence {brDate(c.dueDate)}
                  </p>
                </div>
                <span className="flex items-center gap-1.5 text-[13px] text-fg-muted"><Icon className="size-3.5" />{METHOD_LABELS[c.method]}{c.installments > 1 ? ` ${c.installments}x` : ''}</span>
                <span><Badge tone={CHARGE_STATUS[c.status][1]}>{CHARGE_STATUS[c.status][0]}</Badge></span>
                <span className="text-right tabular-nums"><strong className="text-fg">{formatBRL(c.amountCents)}</strong>
                  {c.paidCents > 0 && c.status !== 'paid' && <span className="block text-[12px] text-fg-muted">pago {formatBRL(c.paidCents)}</span>}</span>
              </div>
            );
          })}
        </Card>
      ) : (
        <EmptyState className="mt-8" icon={<Wallet className="size-6" />} title="Nenhuma cobrança">
          <p>Gere a cobrança na página do orçamento aceito. Antes, conecte a conta do Asaas em Configurações › Pagamentos.</p>
        </EmptyState>
      )}
    </div>
  );
}
